import { createSlice, type PayloadAction } from '@reduxjs/toolkit'

export interface HeaderSummaryAssetRegisterDataState {
  isLoading: boolean
  loadingError: string | undefined
  headerSummaryAssetRegisterData: any | undefined
  httpStatus: number | undefined
}

// Initial state of the slice
const initialState: HeaderSummaryAssetRegisterDataState = {
  isLoading: false,
  loadingError: undefined,
  headerSummaryAssetRegisterData: undefined,
  httpStatus: undefined
}

// Slice to return from action
export const HeaderSummaryAssetRegisterDataListState = createSlice({
  name: 'HeaderSummaryAssetRegisterData',
  initialState,
  reducers: {
    HeaderSummaryAssetRegisterDataLoadingAction: (state) => {
      // clear out the data whenever we reload
      return {
        isLoading: true,
        loadingError: undefined,
        headerSummaryAssetRegisterData: undefined,
        httpStatus: 0
      }
    },
    HeaderSummaryAssetRegisterDataUpdateAction: (state, action: PayloadAction<any>) => {
      return {
        isLoading: false,
        loadingError: undefined,
        headerSummaryAssetRegisterData: action.payload.data,
        httpStatus: action.payload.httpStatus
      }
    },
    HeaderSummaryAssetRegisterDataSocketUpdateAction: (state, action: PayloadAction<any>) => {
      const updatedObj = state.headerSummaryAssetRegisterData
      const devices = (updatedObj?.devices ?? []).map((device: any) => {
        if (device.device_id === action.payload.data.asset_id) {
          // Create a copy of current registerReadings
          const updatedRegisterReadings = [...device.registerReadings]

          // Iterate over new readings from WebSocket
          action.payload.data.registerReadings.forEach((newReading: any) => {
            const existingIndex = updatedRegisterReadings.findIndex(
              (existing) => existing.cimCodeId === newReading.cimCodeId
            )

            if (existingIndex !== -1) {
              // Update the existing reading with new values, preserving the structure
              updatedRegisterReadings[existingIndex] = {
                ...updatedRegisterReadings[existingIndex],
                value: newReading.value,
                formattedValue: newReading.formattedValue,
                colorValue: newReading.colorValue,
                readingDateTime: newReading.readingDateTime,
                readingTime: newReading.readingTime,
                indexValue: newReading.indexValue,
                units: newReading.units,
                registerName: newReading.registerName,
                execution_status: newReading.execution_status ?? updatedRegisterReadings[existingIndex].execution_status ?? null
              }
            } else {
              // If the register doesn't exist in current data, add it
              updatedRegisterReadings.push({
                value: newReading.value,
                formattedValue: newReading.formattedValue,
                colorValue: newReading.colorValue,
                readingDateTime: newReading.readingDateTime,
                readingTime: newReading.readingTime ?? new Date().toISOString(),
                indexValue: newReading.indexValue,
                units: newReading.units,
                registerName: newReading.registerName,
                cimCodeId: newReading.cimCodeId,
                execution_status: newReading.execution_status ?? null
              })
            }
          })

          return {
            ...device,
            registerReadings: updatedRegisterReadings
          }
        } else {
          return device
        }
      })

      return {
        ...state,
        headerSummaryAssetRegisterData: {
          ...state.headerSummaryAssetRegisterData,
          devices
        }
      }
    },
    HeaderSummaryAssetRegisterDataErrorAction: (state, action: PayloadAction<any>) => {
      return {
        ...state,
        isLoading: false,
        loadingError: action.payload,
        httpStatus: action.payload.httpStatus,
        headerSummaryAssetRegisterData: undefined
      }
    }
  }
})

// Actions to export
export const {
  HeaderSummaryAssetRegisterDataLoadingAction,
  HeaderSummaryAssetRegisterDataUpdateAction,
  HeaderSummaryAssetRegisterDataErrorAction,
  HeaderSummaryAssetRegisterDataSocketUpdateAction
} = HeaderSummaryAssetRegisterDataListState.actions

export default HeaderSummaryAssetRegisterDataListState.reducer
